<template>
  <div>
    <b-field grouped class="columns">


      <b-field label="Street" class="column" :type="calculateFieldType('street address')">
        <b-input v-model="address.street1"
                 name="street address"
                 v-validate="{required: true}"
                 :disabled="$attrs['disabled']"
                 required></b-input>
      </b-field>
    </b-field>

    <b-field grouped class="columns">
      <b-field label="Zip Code" class="column is-3" :type="calculateFieldType('zip code')">
        <b-input v-model="address.zip"
                 name="zip code"
                 v-validate="{required: true}"
                 required
                 @input="lookupAddress"
                 :disabled="isFetchingAddress || $attrs['disabled']"></b-input>
      </b-field>

      <b-field label="State" class="column" :type="calculateFieldType('state')">
        <b-select v-model="address.state"
                  name="state"
                  v-validate="{required: true}"
                  required
                  expanded
                  :disabled="isFetchingAddress || $attrs['disabled']"
                  :loading="isFetchingAddress">
          <option
            v-for="(abbreviation, longName) in usStates"
            :value="abbreviation"
            :key="abbreviation">
            {{longName }}
          </option>
        </b-select>
      </b-field>

      <b-field label="City" class="column is-5" :type="calculateFieldType('city')">
        <b-input v-model="address.city"
                 name="city"
                 v-validate="{required: true}"
                 required
                 :disabled="isFetchingAddress || $attrs['disabled']"></b-input>
      </b-field>

    </b-field>
  </div>

</template>

<script>
import { debounce } from 'lodash-es'

import AddressLookup from '@/api-interface/address-lookup'
import usStates from '@/lookup-data/us-states'

const DEBOUCE_DELAY = 200
const defaultAddress = () => ({
  dock: '',
  street1: '',
  street2: '',
  zip: '',
  state: '',
  city: '',
  lat: null,
  lng: null
})

import hasEmptyValues from '@/helpers/hasEmptyValues'
import { isEqual } from 'lodash-es'
export default {
  name: 'AddressLookup',

  data() {
    return {
      blockLookup: false,
      address: defaultAddress(),
      isFetchingAddress: false,
      usStates
    }
  },

  created() {
    const defaults = defaultAddress()
    this.address = hasEmptyValues(this.value) ? defaults : this.value
  },

  computed: {
    isReadyToSearch() {
      return !this.blockLookup && this.address.zip.length >= 5
    }
  },

  props: {
    value: {
      type: Object,
      default: () => ({
        companyAddressStreet1: '',
        companyAddressStreet2: '',
        companyAddressZip: '',
        companyAddressState: '',
        companyAddressCity: ''
      })
    }
  },

  methods: {
    /**
     * Looks up an address based on the zip code. This is done using an external API.
     */
    lookupAddress: debounce(async function() {
      if (!this.isReadyToSearch) {
        return
      }

      this.isFetchingAddress = true

      try {
        const response = await AddressLookup.searchByZip(this.address.zip, this.address.street1)
        this.address.city = response.address.city || ''
        this.address.state = response.address.state || ''
        this.address.lat = response.address.lat
        this.address.lng = response.address.lng
      } catch (error) {
        this.address.city = ''
        this.address.state = ''
        console.error(`invalid zip code -- [${this.address.zip}]: `, error)
      } finally {
        this.isFetchingAddress = false
      }
    }, DEBOUCE_DELAY),

    /**
     * Inform parent of data change
     * @emits input
     */
    notify() {
      this.$emit('input', this.address)
    },

    /**
     *
     * @param {String} fieldName The name of the field in question (this is the name given to vee-validate)
     * @returns {string} CSS class to be applied
     */
    calculateFieldType(fieldName) {
      // TODO: this might make a good candidate for a mix-in
      return this.validationErrors.has(fieldName) ? 'is-danger' : ''
    }
  },

  watch: {
    value: {
      deep: true,
      handler() {
        const defaults = defaultAddress()
        if (!isEqual(this.address, this.value)) {
          // when restoring a value from the passed in prop... temporarily disable the zip lookup functionality while
          // the prop data is being applied. otherwise... it will try to look up the new passed in zip.
          this.blockLookup = true
          this.address = hasEmptyValues(this.value) ? defaults : this.value
          window.setTimeout(() => {
            this.blockLookup = false
          }, DEBOUCE_DELAY * 2)
        }
      }
    },

    address: {
      deep: true,
      handler() {
        if (!isEqual(this.address, this.value)) {
          this.notify()
        }
      }
    }
  }
}
</script>

<style scoped>
/* small reset style for cases where this element is nested within other columns */
.field.is-grouped .field + .field {
  margin-left: -0.75rem;
}
</style>
